import { NgModule } from "@angular/core";
import { NavigationEnd, Router, RouterModule, Routes } from "@angular/router";
import { filter } from "rxjs";
import { AppRoutes, AppRouteParams } from "./enums";
import { Program } from "./services/api";
import { getSafeStorage, isPersistentStorage } from "./utils/safe-storage/safe-storage";
import { SectionPagesPreloadingStrategy } from "./services/section-pages-preloading/section-pages-preloading.strategy";

const STALE_CHUNK_RELOAD_STORAGE_KEY = "jeri-admin:stale-chunk-reload-url";
const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /error loading dynamically imported module/i,
  /failed to fetch dynamically imported module/i,
  /loading chunk .+ failed/i,
  /chunkloaderror/i,
];

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (typeof error !== "object" || error === null) return "";

  const candidate = error as Record<string, unknown>;
  return [
    candidate["message"],
    candidate["reason"],
    candidate["statusText"],
    candidate["originalError"],
    candidate["ngOriginalError"],
    candidate["rejection"],
    candidate["error"],
  ]
    .map(getErrorMessage)
    .filter(message => !!message)
    .join(" ");
}

function isDynamicImportError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

function clearStaleChunkReloadUrl(): void {
  getSafeStorage("sessionStorage").removeItem(STALE_CHUNK_RELOAD_STORAGE_KEY);
}

export function handleRouterError(error: unknown): false {
  if (isDynamicImportError(error)) {
    const currentUrl = window.location.href;
    const sessionStorage = getSafeStorage("sessionStorage");
    const lastReloadUrl = sessionStorage.getItem(STALE_CHUNK_RELOAD_STORAGE_KEY);

    // Without a storage that outlives the reload the guard would be forgotten and the page would reload forever
    if (isPersistentStorage(sessionStorage) && lastReloadUrl !== currentUrl) {
      sessionStorage.setItem(STALE_CHUNK_RELOAD_STORAGE_KEY, currentUrl);
      window.location.reload();
      return false;
    }
  }

  throw error;
}

export const routes: Routes = [
  {
    path: AppRoutes.notFound,
    loadChildren: () => import("./pages/not-found/not-found.module").then(m => m.NotFoundPageModule),
  },
  {
    path: `${Program.IntegrationEnum.Klaviyo}/${AppRoutes.oauth}/${AppRoutes.callback}`,
    loadChildren: () => import("./pages/klaviyo-callback/klaviyo-callback.module").then(m => m.KlaviyoCallbackPageModule),
  },
  {
    path: `:${AppRouteParams.programSlug}`,
    children: [
      {
        path: AppRoutes.onboarding,
        loadChildren: () => import("./pages/onboarding/onboarding.module").then(m => m.OnboardingPageModule),
      },
      {
        // Loaded by Shopify inside a modal opened with DialogService: no admin shell around the dialog
        path: `${AppRoutes.dialog}/:${AppRouteParams.dialogKey}`,
        loadChildren: () => import("./pages/dialog-host/dialog-host.module").then(m => m.DialogHostPageModule),
      },
      {
        path: "",
        loadChildren: () => import("./pages/main-app/main-app.module").then(m => m.MainAppPageModule),
      },
    ],
  },
  {
    path: "**",
    redirectTo: AppRoutes.notFound,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes, {
    // The app initializer routes first (app.initializer.factory): inside Shopify only once the shop's program is
    // confirmed, so a reinstall never opens the not-found page or a page of the previous install
    initialNavigation: "disabled",
    paramsInheritanceStrategy: "always",
    errorHandler: handleRouterError,
    preloadingStrategy: SectionPagesPreloadingStrategy,
  })],
  exports: [RouterModule],
})
export class AppRoutingModule {
  public constructor(router: Router) {
    router.events.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd)).subscribe(() => {
      clearStaleChunkReloadUrl();
    });
  }
}
